import React from 'react'

import { Avatar, useTheme } from '@mui/material'
import { StoreIcon } from 'lucide-react'

import { companyLogoSrc } from 'utils/companyLogo'

/**
 * Initials for a company name — first letter of up to the first two words.
 * "Acme" -> "A", "Acme Supply Co" -> "AS".
 */
export const getCompanyInitials = (name?: string | null): string => {
  if (!name) return ''
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Display avatar for a company. Companies are intentionally a rounded-square
 * (vs. circular person/owner avatars) — the shape signals "organization" and
 * preserves logo aspect ratios (see VERA-880).
 *
 * Fallback order: logo image -> initials -> store glyph (no name available).
 * Uses MUI's built-in `<Avatar>` children fallback, so a broken/404 logo URL
 * degrades to initials rather than a broken-image icon.
 *
 * THREE distinct states render no image, and they must all look the same:
 * `logo` is null, `logo` is a non-URL string (`''`, or a seed placeholder like
 * "Louis Vuitton Logo" — `companyLogoSrc` maps both to `undefined`), and `logo`
 * is a well-formed URL that fails to load. Only the third reaches the DOM as an
 * image at all, which is why the white logo plate is attached to the `<img>`
 * rather than to the tile (see the note on `.MuiAvatar-img` below).
 */
export const CompanyAvatar = ({
  logo,
  name,
  size = 48,
  radius = '6px',
}: {
  logo?: string | null
  name?: string | null
  size?: number
  /**
   * Corner radius. Overridable because a company thumbnail in a TABLE ROW has to match the
   * product and package thumbnails beside it (8px), while the 6px default is what the rest of
   * the app uses. Defaulted so no existing call site changes.
   */
  radius?: string
}) => {
  const theme = useTheme()
  const initials = getCompanyInitials(name)
  // Only treat the logo as an image when it's an actual URL. Placeholder/garbage
  // strings (e.g. seed "Louis Vuitton Logo") would otherwise be requested and —
  // because the SPA returns index.html (200) for unknown paths — "load" as a
  // broken image instead of falling back to initials.
  // The test lives in `utils/companyLogo` because the setup checklist needs the same
  // answer, and when it hand-rolled its own the two disagreed (§36).
  const logoSrc = companyLogoSrc(logo)

  return (
    <Avatar
      variant="rounded"
      src={logoSrc}
      alt={name || undefined}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        border: `1px solid ${theme.palette.grey[200]}`,
        // The tile's own background is ALWAYS the theme surface. The white logo plate lives
        // on the `<img>` instead (below) — see the long note there for why that one-line
        // move is the whole fix.
        bgcolor: theme.palette.grey[50],
        '& .MuiAvatar-img': {
          // Logos are art, not photos: contain the whole mark and leave a little breathing
          // room rather than cropping it to fill a square.
          objectFit: 'contain',
          padding: `${Math.max(2, Math.round(size * 0.1))}px`,
          // ── The white logo plate, and why it belongs HERE and not on the root ──────
          //
          // A real logo always sits on a LIGHT plate, in both themes; only the
          // initials/glyph fallback follows the theme.
          //
          // Brand marks are authored for a specific background — overwhelmingly white — and
          // most are dark ink on transparency. On a dark surface those vanish entirely, which
          // is why Louis Vuitton's logo was invisible in dark mode. The plate makes any logo
          // legible with zero per-brand work and, unlike a CSS invert/filter, preserves the
          // brand's exact colour (inverting turns a red mark cyan, which is worse than the
          // problem).
          //
          // VRG-560: the plate used to be `bgcolor: logoSrc ? white : grey[50]` on the ROOT,
          // decided from the URL STRING, before anything knew whether that URL loads. When it
          // 404s MUI drops the `<img>` and renders the initials instead — onto a plate that is
          // still white, because the string was still truthy. In dark mode the initials are
          // `grey[600]` = `#d4d4d8`, which is 1.48:1 on white against a 4.5:1 minimum. The tile
          // reads as blank. Three live companies hit it, one of them pointing at
          // `via.placeholder.com`, a service dead since 2024.
          //
          // Moving the plate onto the img makes the plate follow the IMAGE rather than the
          // string, which is the property we actually wanted. MUI's `Avatar` renders the `<img>`
          // only while `loaded !== 'error'` (`useLoaded`), so on failure the element — and with
          // it the plate — leaves the DOM and the initials land on `grey[50]`.
          //
          // Measured in the browser 2026-09-06 (`#d4d4d8` on `#18181b` dark, `#575a60` on
          // `#f4f5f5` light), every state, both modes:
          //
          //   dark  1.48:1 -> 11.99:1     light  6.92:1 -> 6.33:1
          //
          // Light gets marginally WORSE, and that is the correct trade: light was never the
          // failure (6.92:1 already passed), and 6.33:1 still clears the 4.5:1 minimum with
          // room. Dark is the case that was unreadable. An earlier revision of this comment
          // claimed 6.63:1 for light — a number nobody had measured; the real value is 6.33:1.
          //
          // The three states that must not have a plate (null, empty string, failed load) get
          // that for free, from one mechanism, with no `onError` state to keep in sync and no
          // flash on the working path (`.MuiAvatar-img` is width/height 100%, so its background
          // paints the same box the root's did, padding included — confirmed `box-sizing:
          // border-box`, so a 44px tile's plate is the full 44x44 — clipped by the root's
          // `overflow: hidden` to the same `radius`).
          //
          // Known limitation, accepted deliberately: a brand shipping a WHITE-only mark is
          // invisible on the plate. CONFIRMED 2026-09-06, not assumed — pointing an avatar's
          // `<img>` at a white-on-transparent SVG renders a blank white square (1.00:1). It is
          // precisely the brand that would upload a dark-mode variant, so the fix is a
          // `logo_dark` column layered on top of this default, not abandoning the plate.
          // (Honest caveat on "minority": 0 of the 3 logos in the local dataset are white-only,
          // which is too small a sample to call a rate — the limitation is real and unmeasured,
          // not real and quantified.)
          backgroundColor: theme.palette.common.white,
        },
        color: theme.palette.grey[600],
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
      }}
    >
      {initials || (
        <StoreIcon strokeWidth={1} size={Math.round(size * 0.5)} color={theme.palette.grey[400]} />
      )}
    </Avatar>
  )
}
